import {
  AnimationClip,
  BoxGeometry,
  Color,
  DirectionalLight,
  LinearToneMapping,
  MathUtils,
  Mesh,
  MeshStandardMaterial,
  MeshToonMaterial,
  Object3D,
  PCFSoftShadowMap,
  PerspectiveCamera,
  Scene,
  ShaderMaterial,
  sRGBEncoding,
  Uniform,
  Vector3,
  WebGLRenderer,
  type WebGLRendererParameters,
} from 'three'
import { OutlineMaterial } from '@/components/rabbit/customMaterials/OutlineMaterial'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { Pane } from 'tweakpane'
import { ThreePerf } from 'three-perf'
import glbUrl from '@/assets/gltf/advergaming.glb?url'
import { GLTF, GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import breakpoints from '@/data/breakpoints.json'
import gsap from 'gsap'
import CustomWiggle from 'gsap/CustomWiggle'
import CustomEase from 'gsap/CustomEase'

import { Loop } from './loop'
import { updateMaterials } from './utils'
import {
  COLOR_HEX_BLACK,
  COLOR_HEX_GREEN,
  COLOR_HEX_RED,
} from '@/components/rabbit/World'
import { atom, WritableAtom } from 'nanostores'
import { Store } from 'pinia'
import { AdvergamingState } from '@/stores/advergaming'

interface CoreConstructorParameters {
  id: string
  store: Store<'advergaming', AdvergamingState>
}

interface CoreInitParameters {
  container: HTMLElement
  renderer?: WebGLRendererParameters
}

const COLOR_HEX_WHITE = 0xffffff
const COLOR_HEX_BLOOD = 0xd90000
const COLOR_HEX_BG = 0x3c3232
const COLOR_CSS_BG = '#3c3232'
const PARTICLE_SIZE = 0.2
const DIRECTIONAL_LIGHT_POSITION = new Vector3(-6, 7, 3)
const DIRECTIONAL_LIGHT_COLOR = new Color(1, 1, 1)
const WIGGLE_NAME_1 = 'advergaming-wiggle-1'
const WIGGLE_NAME_2 = 'advergaming-wiggle-2'
const CARROT_ROTATION_SPEED = 0.0025
const RABBIT_RESPAWN_SCALE_EASE = 'elastic.out(1,0.5)'
const RABBIT_RESPAWN_SCALE_DURATION = 1
const SHADOW_INTENSITY = 0.25

const ASSETS_KEYS = [
  'rabbit',
  'carrot',
  'chili',
  'tile',
  'tile-hole-green',
  'tile-hole-red',
  'block',
  'block-broken',
  'trap',
  'dynamite',
  'rock-1',
  'rock-2',
  'rock-3',
  'rock-4',
  'rock-floor',
  'grass',
] as const

const RABBIT_STATE = {
  DEAD: 'dead',
  ALIVE: 'alive',
} as const

/**
 * Base class to extends advergaming minigames from.
 * It setups: threejs, debug (using ?debug query param in URL), assets loading.
 */
class Core {
  protected id: string

  protected containerEl: CoreInitParameters['container']
  protected intersectionObserver!: IntersectionObserver
  protected isContainerWithinViewport = false

  protected loader = new GLTFLoader()
  protected assets = new Map<(typeof ASSETS_KEYS)[number], Object3D>() // Kind of hard to read but having ASSETS_KEYS being a const helps autocomplete
  protected particles!: {
    geometry: BoxGeometry
    white: Mesh<any, any>
    groundish: Mesh<any, any>
    red: Mesh<any, any>
    blood: Mesh<any, any>
    green: Mesh<any, any>
  }
  protected animationClips: {
    brokenBlock: AnimationClip[]
    trap: AnimationClip[]
  } = {
    brokenBlock: [],
    trap: [],
  } as const
  // protected blockShaderMaterial!: ShaderMaterial

  protected renderer!: WebGLRenderer

  protected scene!: Scene

  protected loop!: Loop

  public store!: {
    $score: WritableAtom<number> // Internal (scoped to game instance) reactive value
    $combo: WritableAtom<number> // Internal (scoped to game instance) reactive value
    $deaths: WritableAtom<number> // Internal (scoped to game instance) reactive value
    $interactedWith: WritableAtom<boolean> // Internal (scoped to game instance) reactive value
    watchers: (() => void)[] // List of watchers that needs to be disposed when disposing of game instance
    globalPinia: CoreConstructorParameters['store'] // Pinia store shared between all games extended from Core base class
  }

  protected camera!: PerspectiveCamera
  protected debugCamera?: PerspectiveCamera
  protected debugCameraControls?: OrbitControls
  protected readonly cameraPositionDesktop = new Vector3(20, 20, 20)
  protected readonly cameraPositionMobile = new Vector3(10, 10, 10)
  protected readonly cameraBreakpoint: keyof typeof breakpoints = 'm'
  protected cameraShakeAnimation?: gsap.core.Timeline

  protected dirLight!: DirectionalLight
  protected antiLight!: DirectionalLight

  protected outlineMaterial = new OutlineMaterial({
    color: 0x332e2e,
    size: 0.03,
  })

  protected rabbitState: (typeof RABBIT_STATE)[keyof typeof RABBIT_STATE] =
    RABBIT_STATE.ALIVE

  protected debug = false
  public useDebugCamera = false
  protected pane!: Pane
  protected perf?: ThreePerf

  constructor(params: CoreConstructorParameters) {
    this.onRender = this.onRender.bind(this)
    this.onFixedUpdate = this.onFixedUpdate.bind(this)
    this.onRenderBegin = this.onRenderBegin.bind(this)
    this.onRenderEnd = this.onRenderEnd.bind(this)
    this.onResize = this.onResize.bind(this)
    this.onDocumentVisibilityChange = this.onDocumentVisibilityChange.bind(this)

    this.id = params.id

    this.initStore(params.store)
  }

  public async init(params: CoreInitParameters) {
    this.containerEl = params.container

    this.initLoop()
    this.initParticles()
    this.initRenderer(params?.renderer)
    this.initCamera()
    this.initScene()
    this.initLights()
    // this.initBlocShaderMaterial()
    this.initDebug()
    this.initPerf()

    await this.loadAssets()

    // Give a chance to children to run custom logic that relies on basic scene setup before loop starts
    this.afterInit()

    this.updateCamera()
    this.setSize()

    window.addEventListener('resize', this.onResize)

    document.addEventListener(
      'visibilitychange',
      this.onDocumentVisibilityChange
    )

    this.initIntersectionObserver()
  }

  protected afterInit() {}

  protected initStore(globalPinia: CoreConstructorParameters['store']) {
    this.store = {
      $score: atom(0),
      $combo: atom(0),
      $deaths: atom(0),
      $interactedWith: atom(false),
      watchers: [],
      globalPinia,
    }

    this.store.watchers.push(
      this.store.$deaths.listen(() => {
        this.store.globalPinia.deaths += 1
      })
    )

    this.store.watchers.push(
      this.store.$score.listen((value, oldValue) => {
        // In order to increment the global carrot count, we add the difference everytime score changes.
        const difference = value - (oldValue ?? 0)
        this.store.globalPinia.carrots += difference
      })
    )
  }

  protected initLoop() {
    // ℹ️ Note: loop is not started right away because we do not want it to run unless the game is within viewport.
    // The starting and stopping of the loop is managed using the intersectionObserverCallback.
    this.loop = new Loop()
    this.loop.setRender(this.onRender)
    this.loop.setUpdate(this.onFixedUpdate)
    this.loop.setBegin(this.onRenderBegin)
    this.loop.setEnd(this.onRenderEnd)
  }

  protected initRenderer(params?: WebGLRendererParameters) {
    this.renderer = new WebGLRenderer(params)

    this.renderer.outputEncoding = sRGBEncoding
    this.renderer.toneMapping = LinearToneMapping
    this.renderer.toneMappingExposure = 1
    this.renderer.shadowMap.enabled = true
    this.renderer.shadowMap.type = PCFSoftShadowMap
    this.renderer.setClearColor(COLOR_HEX_BG)

    this.renderer.domElement.style.width = '100%'
    this.renderer.domElement.style.height = '100%'
    this.renderer.domElement.style.display = 'block'
    this.containerEl.appendChild(this.renderer.domElement)
  }

  protected initCamera() {
    this.camera = new PerspectiveCamera()

    if (this.debug) {
      this.debugCamera = this.camera.clone()
      this.debugCameraControls = new OrbitControls(
        this.debugCamera,
        this.renderer.domElement
      )
      this.debugCameraControls.enableDamping = true
    }

    this.updateCamera()

    // Camera shake
    gsap.registerPlugin(CustomEase, CustomWiggle)
    CustomWiggle.create(WIGGLE_NAME_1, { wiggles: 3 })
    CustomWiggle.create(WIGGLE_NAME_2, { wiggles: 4 })
  }

  protected initScene() {
    this.scene = new Scene()
  }

  protected initDebug() {
    this.debug = location.search.indexOf('debug') !== -1

    if (!this.debug) {
      return
    }

    this.pane = new Pane({
      container: this.containerEl,
    })

    this.pane.element.style.position = 'absolute'
    this.pane.element.style.top = '0px'
    this.pane.element.style.right = '0px'

    this.pane.addBinding(this, 'useDebugCamera')

    this.pane
      .addBinding(
        {
          shadowIntensity: 0.25,
        },
        'shadowIntensity',
        {
          min: 0,
          max: 1,
        }
      )
      .on('change', ({ value }) => {
        this.dirLight.intensity = value
        this.antiLight.intensity = 1 - value
      })
  }

  protected initPerf() {
    if (!this.debug) {
      return
    }

    this.perf = new ThreePerf({
      anchorX: 'left',
      anchorY: 'top',
      domElement: this.containerEl,
      renderer: this.renderer,
    })

    // We'll have multiple game per page so we don't want duplicate IDs
    this.perf.ui.wrapper.id = `three-perf-ui_${this.id}`

    // Overwrite graph position (fixed by default) otherwise we would have multiple
    // overlapping graphs if multiple games are instanciated.
    this.perf.ui.wrapper.style.position = 'absolute'
  }

  /**
   * Same light setup shared between all minigames.
   * Works in theory, we'll see if this is a god idea or not.
   */
  protected initLights() {
    this.dirLight = new DirectionalLight(
      DIRECTIONAL_LIGHT_COLOR,
      SHADOW_INTENSITY
    )
    this.dirLight.position.copy(DIRECTIONAL_LIGHT_POSITION)
    this.dirLight.castShadow = true
    this.dirLight.shadow.mapSize.width = 1024
    this.dirLight.shadow.mapSize.height = 1024
    this.dirLight.shadow.camera.near = 6
    this.dirLight.shadow.camera.far = 30
    this.dirLight.shadow.camera.left = -20
    this.dirLight.shadow.camera.right = 20
    this.dirLight.shadow.camera.bottom = -20
    this.dirLight.shadow.camera.top = 20
    this.dirLight.shadow.bias = -0.01
    this.scene.add(this.dirLight)

    // Note: LightShadow.intensity does not exists in current version of threejs so we use a two lights hack to work around
    // https://github.com/mrdoob/three.js/pull/14087#issuecomment-431003830
    this.antiLight = this.dirLight.clone()
    this.antiLight.castShadow = false
    this.antiLight.intensity = 1 - SHADOW_INTENSITY
    this.scene.add(this.antiLight)
  }

  /**
   * Instanciate an IO that will monitor the visibility of the dom container
   * and toggle the enabled flag accordingly. Preventing the game from running
   * when not in viewport.
   */
  protected initIntersectionObserver() {
    this.intersectionObserverCallback =
      this.intersectionObserverCallback.bind(this)

    this.intersectionObserver = new IntersectionObserver(
      this.intersectionObserverCallback,
      {
        root: window.document,
        rootMargin: '0px 0px 0px 0px',
        threshold: 0,
      }
    )

    this.intersectionObserver.observe(this.containerEl)
  }

  protected intersectionObserverCallback(entries: IntersectionObserverEntry[]) {
    // ℹ️ We should always have only one entry, this.containerEl.
    // Only check this one instead of iterating over each entries to avoid potential
    // bugs if another entry is registered somehow.
    if (!entries[0]) {
      return
    }

    // Note: save this information as we might need it in onDocumentVisibilityChange.
    this.isContainerWithinViewport = entries[0].isIntersecting

    if (this.isContainerWithinViewport) {
      this.loop.start()
    } else {
      this.loop.stop()
    }
  }

  protected initParticles() {
    const geometry = new BoxGeometry(
      PARTICLE_SIZE,
      PARTICLE_SIZE,
      PARTICLE_SIZE
    )

    this.particles = {
      geometry,
      white: new Mesh(
        geometry,
        new MeshToonMaterial({ color: COLOR_HEX_WHITE })
      ),
      groundish: new Mesh(
        geometry,
        new MeshToonMaterial({
          color: new Color(0x262323),
        })
      ),
      red: new Mesh(geometry, new MeshToonMaterial({ color: COLOR_HEX_RED })),
      blood: new Mesh(
        geometry,
        new MeshToonMaterial({ color: COLOR_HEX_BLOOD })
      ),
      green: new Mesh(
        geometry,
        new MeshToonMaterial({ color: COLOR_HEX_GREEN })
      ),
    }
  }

  /**
   * Returns promise that resolve when every assets needed for all the minigames have loaded.
   * Ideally, every asset for every minigames will be in the same shared glb file.
   * We load it, parse the scene (updating materials and processing stuff that needs to be processed)
   * and store a reference to assets within a map easily accessible from child games.
   */
  protected loadAssets() {
    return new Promise(resolve =>
      this.loader.load(glbUrl, gltf => {
        updateMaterials(gltf.scene, material => {
          if (material.name === 'bgr.gradient') {
            const mat = new MeshStandardMaterial({
              color: material.color,
              name: material.name,
              map: material.map,
              flatShading: true,
            })

            return mat
          }

          // Some materials do not match the visuals of existing experiences (skating rabbit, maze...)
          // Instead of updating the 3D models, we overwrite the colors of materials when loading models.
          // Colors are copy pasted from src/components/rabbit/World.ts.
          const color = (() => {
            switch (material.name) {
              case 'bgr':
                return new Color(COLOR_HEX_BG).convertSRGBToLinear()
              case 'green':
                return COLOR_HEX_GREEN
              case 'red':
                return COLOR_HEX_RED
              case 'black':
                return COLOR_HEX_BLACK
              default:
                return material.color
            }
          })()

          return new MeshToonMaterial({
            color,
            name: material.name,
            map: material.map,
          })
        })

        // Iterate through first level children and reset their position
        gltf.scene.children.forEach(object => {
          object.position.set(0, 0, 0)
        })

        const rabbit = this.prepareRabbit(gltf.scene)
        this.assets.set('rabbit', rabbit)

        const carrot = this.prepareCarrot(gltf.scene)
        this.assets.set('carrot', carrot)

        const dynamite = this.prepareDynamite(gltf.scene)
        this.assets.set('dynamite', dynamite)

        const trap = this.prepareTrap(gltf)
        this.assets.set('trap', trap)

        const tile = this.prepareTile(gltf.scene)
        this.assets.set('tile', tile)

        const tileHoleRed = this.prepareTileHole(gltf.scene, 'tileHoleRed')
        this.assets.set('tile-hole-red', tileHoleRed)

        const tileHoleGreen = this.prepareTileHole(gltf.scene, 'tileHoleGreen')
        this.assets.set('tile-hole-green', tileHoleGreen)

        const block = this.prepareBlock(gltf)
        this.assets.set('block', block)

        const brokenBlock = this.prepareBrokenBlock(gltf.scene)
        this.assets.set('block-broken', brokenBlock)

        const rock1 = this.prepareRock(gltf.scene, 'rock1')
        this.assets.set('rock-1', rock1)

        const rock2 = this.prepareRock(gltf.scene, 'rock2')
        this.assets.set('rock-2', rock2)

        const rock3 = this.prepareRock(gltf.scene, 'rock3')
        this.assets.set('rock-3', rock3)

        const rock4 = this.prepareRock(gltf.scene, 'rock4')
        this.assets.set('rock-4', rock4)

        const rockFloor = this.prepareRock(gltf.scene, 'RockPlatform')
        this.assets.set('rock-floor', rockFloor)

        const grass = this.prepareGrass(gltf.scene)
        this.assets.set('grass', grass)

        // No chili ❌🌶️
        // this.assets.set('chili', gltf.scene.getObjectByName('chili')!)

        resolve(true)
      })
    )
  }

  //   protected initBlocShaderMaterial() {
  //     const color2 = new Color(COLOR_HEX_BG)
  //     const color1 = color2.clone().offsetHSL(0, 0.1, 0.15)

  //     this.blockShaderMaterial = new ShaderMaterial({
  //       fragmentShader: `
  //           varying vec2 vUv;

  //           uniform vec3 uColor1;
  //           uniform vec3 uColor2;

  //           void main() {
  //             vec3 color = mix(uColor1, uColor2, pow(vUv.y, 0.1));
  //             color += pow(1.- vUv.y, 5.) * .2;
  //             gl_FragColor = vec4(color, 1.);
  //           }
  // `,
  //       vertexShader: `
  //         varying vec2 vUv;

  //         void main() {
  //           vUv = uv;
  //           gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  //         }
  //       `,
  //       uniforms: {
  //         uColor1: new Uniform(color1),
  //         uColor2: new Uniform(color2),
  //       },
  //     })
  //   }

  protected prepareRabbit(scene: Object3D) {
    const rabbit = scene.getObjectByName('rabbit') as Mesh

    const rabbitBody = rabbit.getObjectByName('body') as Mesh
    const rabbitEarLeft = rabbit.getObjectByName('earLeft') as Mesh
    const rabbitEarRight = rabbit.getObjectByName('earRight') as Mesh
    const rabbitTail = rabbit.getObjectByName('tail') as Mesh

    this.addOutline(rabbitBody)
    this.addOutline(rabbitEarLeft)
    this.addOutline(rabbitEarRight)
    this.addOutline(rabbitTail)

    rabbit.traverse(o => {
      o.castShadow = true
    })

    return rabbit
  }

  protected prepareCarrot(scene: Object3D) {
    const carrot = scene.getObjectByName('carrot') as Mesh
    this.addOutline(carrot)

    carrot.traverse(o => {
      o.castShadow = true
    })

    return carrot
  }

  protected prepareDynamite(scene: Object3D) {
    const dynamite = scene.getObjectByName('dynamite') as Mesh

    const cylinder = dynamite.getObjectByName('Cylinder002') as Mesh

    dynamite.traverse(o => {
      o.castShadow = true
    })

    this.addOutline(cylinder)

    return dynamite
  }

  protected prepareTrap(gltf: GLTF) {
    const trapHolder = gltf.scene.getObjectByName('trapHolder') as Object3D

    ;['trapAction', 'trapTeeth1Action', 'TrapTeeth2Action'].forEach(name => {
      this.animationClips.trap.push(
        AnimationClip.findByName(gltf.animations, name)
      )
    })

    const trap = trapHolder.getObjectByName('trap') as Mesh
    const trapTeeth1 = trapHolder.getObjectByName('trapTeeth1') as Mesh
    const trapTeeth2 = trapHolder.getObjectByName('trapTeeth2') as Mesh

    trap.traverse(o => {
      o.castShadow = true
    })

    this.addOutline(trap)
    this.addOutline(trapTeeth1)
    this.addOutline(trapTeeth2)

    return trapHolder
  }

  protected prepareTile(scene: Object3D) {
    const tile = scene.getObjectByName('tile')!

    tile.traverse(o => {
      o.receiveShadow = true
    })

    return tile
  }

  protected prepareTileHole(scene: Object3D, name: string) {
    const tile = scene.getObjectByName(name)!

    tile.traverse(o => {
      o.receiveShadow = true
    })

    return tile
  }

  protected prepareBlock(gltf: GLTF) {
    const block = gltf.scene.getObjectByName('bloc') as Mesh

    block.traverse(o => {
      o.castShadow = true
      o.receiveShadow = true
    })
    ;[
      'blocPart1Action',
      'blocPart2Action',
      'blocPart3Action',
      'blocPart4Action',
    ].forEach(name => {
      this.animationClips.brokenBlock.push(
        AnimationClip.findByName(gltf.animations, name)
      )
    })

    return block
  }

  protected prepareBrokenBlock(scene: Object3D) {
    const block = scene.getObjectByName('brokenBlocHolder') as Mesh

    block.traverse(o => {
      o.castShadow = true
      o.receiveShadow = true
    })

    this.addOutline(block)

    return block
  }

  protected prepareRock(scene: Object3D, name: string) {
    const rock = scene.getObjectByName(name) as Mesh

    return rock
  }

  protected prepareGrass(scene: Object3D) {
    const grass = scene.getObjectByName('grass') as Mesh

    grass.traverse(o => {
      o.castShadow = true
      o.receiveShadow = true
    })

    return grass
  }

  addOutline(origin: Mesh) {
    const outline = origin.clone()

    outline.children = []
    outline.position.set(0, 0, 0)
    outline.rotation.x = 0
    outline.rotation.y = 0
    outline.rotation.z = 0
    outline.scale.set(1, 1, 1)
    outline.material = this.outlineMaterial
    origin.add(outline)

    return outline
  }

  /**
   * Returns one of the two pre-defined position based on the viewport width and camera breakpoint.
   * Child games should overwrite those positions to suit game visuals needs.
   */
  protected getCameraPosition() {
    if (window.innerWidth < breakpoints[this.cameraBreakpoint]) {
      return this.cameraPositionMobile
    }

    return this.cameraPositionDesktop
  }

  protected particlesDustPoof(
    pos: Vector3,
    parent?: Object3D,
    scale: number = 1
  ) {
    this.spawnParticles({
      pos,
      count: 10,
      mesh: this.particles.white,
      scale: {
        min: 0.5 * scale,
        max: 1 * scale,
      },
      xRandomSpread: 1 * scale,
      yRandom: {
        min: 0 * scale,
        max: 1 * scale,
      },
      zRandom: {
        min: -0.5 * scale,
        max: 0.5 * scale,
      },
      duration: {
        min: 0.5 * scale,
        max: 1 * scale,
      },
      parent,
    })
  }

  protected particlesCarrot(
    pos: Vector3,
    parent?: Object3D,
    scale: number = 1
  ) {
    const params = {
      pos,
      scale: {
        min: 1 * scale,
        max: 3 * scale,
      },
      xRandomSpread: 2 * scale,
      yRandom: {
        min: 0 * scale,
        max: 2 * scale,
      },
      zRandom: {
        min: -2 * scale,
        max: 2 * scale,
      },
      duration: {
        min: 0.5 * scale,
        max: 1 * scale,
      },
      parent,
    }

    this.spawnParticles({
      count: 20,
      mesh: this.particles.red,
      ...params,
    })

    this.spawnParticles({
      count: 6,
      mesh: this.particles.green,
      ...params,
    })
  }

  protected particlesDeadRabbit(pos: Vector3, scale: number = 1) {
    this.spawnParticles({
      pos,
      count: 50,
      mesh: this.particles.blood,
      scale: {
        min: 2 * scale,
        max: 3 * scale,
      },
      xRandomSpread: 2 * scale,
      yRandom: {
        min: 0 * scale,
        max: 2 * scale,
      },
      zRandom: {
        min: -2 * scale,
        max: 2 * scale,
      },
      duration: {
        min: 0.5 * scale,
        max: 0.75 * scale,
      },
    })

    this.spawnParticles({
      pos,
      count: 50,
      mesh: this.particles.green,
      scale: {
        min: 3 * scale,
        max: 5 * scale,
      },
      xRandomSpread: 2 * scale,
      yRandom: {
        min: 0 * scale,
        max: 2 * scale,
      },
      zRandom: {
        min: -2 * scale,
        max: 2 * scale,
      },
      duration: {
        min: 0.5 * scale,
        max: 0.75 * scale,
      },
    })
  }

  /**
   * Mostly copy pasted from src/components/rabbit/World.ts
   * Main difference: meshes are not pooled but cloned each time.
   * At our level, this is fine because:
   * - meshes are clone, no new instances (this is super important)
   * - clones are removed from scene when they disapear
   * This is not the most performant way of doing it but it does not negatively impact performances so I estimate that it is fine.
   * Draw calls and triangle count will go up during animation but will go right back down.
   */
  protected spawnParticles({
    pos,
    count,
    mesh,
    scale,
    duration,
    xRandomSpread,
    yRandom,
    zRandom,
    parent,
    invertScale,
  }: {
    count: number
    mesh: Mesh<any, any>
    scale: {
      min: number
      max: number
    }
    duration: {
      min: number
      max: number
    }
    xRandomSpread: number
    yRandom: {
      min: number
      max: number
    }
    zRandom: {
      min: number
      max: number
    }
    pos: Vector3
    parent?: Object3D
    invertScale?: boolean
  }) {
    const particles: Mesh<any, any>[] = []

    for (let i = 0; i < count; i++) {
      const m = mesh.clone()
      m.scale.set(0, 0, 0)
      particles.push(m)
    }

    ;(parent ?? this.scene).add(...particles)

    particles.forEach(p => {
      p.position.x = pos.x
      p.position.y = pos.y
      p.position.z = pos.z

      const scalarScale = MathUtils.randFloat(scale.min, scale.max)
      p.scale.setScalar(invertScale ? 0 : scalarScale)

      const randomDuration = MathUtils.randFloat(duration.min, duration.max)

      const TL = gsap.timeline({
        onComplete: () => {
          p.removeFromParent()
        },
      })

      TL.to(
        p.position,
        {
          x: pos.x + MathUtils.randFloatSpread(xRandomSpread * 2),
          y: pos.y + MathUtils.randFloat(yRandom.min, yRandom.max),
          z: pos.z + MathUtils.randFloat(zRandom.min, zRandom.max),
          duration: randomDuration,
          ease: 'power2.out',
        },
        0
      )

      TL.to(
        p.rotation,
        {
          x: MathUtils.randFloatSpread(Math.PI),
          y: MathUtils.randFloatSpread(Math.PI),
          z: MathUtils.randFloatSpread(Math.PI),
          duration: randomDuration,
          ease: 'power4.out',
        },
        0
      )

      TL.to(
        p.scale,
        {
          x: invertScale ? scalarScale : 0,
          y: invertScale ? scalarScale : 0,
          z: invertScale ? scalarScale : 0,
          duration: randomDuration,
          ease: 'power4.out',
        },
        0
      )

      if (invertScale) {
        TL.to(p.scale, {
          x: 0,
          y: 0,
          z: 0,
          duration: randomDuration * 0.25,
          ease: 'power4.out',
        })
      }
    })
  }

  protected updateCamera() {
    const { width, height } = this.getDimensions()
    const aspect = width / height

    this.camera.aspect = aspect
    this.camera.updateProjectionMatrix()

    this.camera.position.copy(this.getCameraPosition())
    this.camera.lookAt(0, 0, 0)

    if (this.debugCamera) {
      this.debugCamera.aspect = aspect
      this.debugCamera.updateProjectionMatrix()
      this.debugCamera.position.copy(this.camera.position)
    }

    if (this.debugCameraControls) {
      this.debugCameraControls.target.set(0, 0, 0)
      this.debugCameraControls.update()
    }
  }

  private onRenderBegin(_timestamp: number, _dt: number) {
    this.perf?.begin()
  }

  private onRenderEnd(_fps: number, _panic: boolean) {
    this.perf?.end()
  }

  /**
   * Called every frame. Render on screen.
   */
  protected onRender(_interp: number, _dt: number) {
    if (this.useDebugCamera && this.debugCameraControls && this.debugCamera) {
      this.debugCameraControls.update()
      this.renderer.render(this.scene, this.debugCamera)
    } else {
      this.renderer.render(this.scene, this.camera)
    }
  }

  /**
   * Called on fixed timestep.
   * Should be overwritten by child game.
   */
  protected onFixedUpdate(timestep: number) {}

  protected onResize() {
    this.setSize()
    this.updateCamera()
  }

  /**
   * Toogle loop when tab looses / regain focus.
   * Before restarting the loop, we make sure that game is actually visible.
   */
  protected onDocumentVisibilityChange() {
    if (document.visibilityState === 'hidden') {
      this.loop.stop()
    } else if (this.isContainerWithinViewport) {
      this.loop.start()
    }
  }

  protected getDimensions() {
    const BCR = this.renderer.domElement.parentElement!.getBoundingClientRect()

    const DPR = window.devicePixelRatio

    const width = BCR.width * DPR
    const height = BCR.height * DPR

    return { width, height }
  }

  protected setSize() {
    const { width, height } = this.getDimensions()

    this.renderer.setSize(width, height, false)
  }

  protected dispose() {
    this.loop.dispose()

    document.removeEventListener(
      'visibilitychange',
      this.onDocumentVisibilityChange
    )

    window.removeEventListener('resize', this.onResize)

    this.store.watchers.forEach(stop => {
      stop()
    })
  }

  protected cameraShake(amplitude: number) {
    this.cameraShakeAnimation?.kill()

    this.cameraShakeAnimation = gsap
      .timeline()
      .to(
        this.camera.position,
        {
          duration: 0.25,
          x: `+=${amplitude}`,
          ease: WIGGLE_NAME_1,
        },
        0
      )
      .to(
        this.camera.position,
        {
          duration: 0.25,
          z: `+=${amplitude}`,
          ease: WIGGLE_NAME_2,
        },
        0
      )
  }
}

export {
  Core,
  CARROT_ROTATION_SPEED,
  RABBIT_STATE,
  RABBIT_RESPAWN_SCALE_EASE,
  RABBIT_RESPAWN_SCALE_DURATION,
  COLOR_CSS_BG,
}
export type { CoreInitParameters, CoreConstructorParameters }
